import { Button, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import MuiInput from "@shared/ui/mui-input/MuiInput";
import { StyledEditWork } from "./EditWork.styled";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { deleteEmptyFieldsFromObject, groupBy } from "@shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { getToastErrorMessage } from "@shared/lib/errors"
import AlertDialog from "@shared/ui/alert-dialog/AlertDialog";
import { Work, WorkEdit, WorkEditSchema } from "@models/work";
import { WorkContext } from "./WorkContext";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from "@emotion/react";
import { setCustomDataArray } from "@features/tickets/shared/utils/customData";
import { useGetAccountStructureQuery } from "@features/structures/api/accountsStructure.api";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { DocumentWorkEdit } from "@models/document-work";
import { editLastBread, getLastAvailableLink } from "@features/tickets/shared/utils/breadcrumbs";
import { useDeleteWorkMutation, useSaveWorkMutation } from "@features/tickets/api/works.api";


function EditWork() {

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: workStructure } = useGetAccountStructureQuery("עבודה");

  const structureSortedByTitle = groupBy(workStructure, "account_title")
  const customFieldsExist = structureSortedByTitle?.["מותאם אישית"]?.find(structure => !!structure.show_form === true);

  const { work, setWork } = useContext(WorkContext);
  const [saveWork] = useSaveWorkMutation();
  const [deleteWork] = useDeleteWorkMutation();


  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const form = useForm<WorkEdit>({
    resolver: zodResolver(WorkEditSchema),
    defaultValues: {
      ...deleteEmptyFieldsFromObject(work)
    }
  });


  useEffect(() => {
    if (work) {
      setFormValues(work);
    }
  }, [work]);


  function setFormValues(work: Work) {
    // Object.keys(work).forEach(key => {
    //   form.setValue(key as keyof Work, work[key as keyof Work]);
    // });

    form.reset(work);
  }


  const saveTicket = async () => {

    try {

      // console.log("saveTicket FormValues: ", form.getValues());

      // form.setValue("card_number", ticketNumber);

      const customDataArray = form.getValues("custom_data");
      setCustomDataArray(customDataArray);
      form.setValue("custom_data", customDataArray);

      form.handleSubmit(onSubmit)();

      // console.log("errors: ", form.formState.errors);

    } catch (error) {
      console.error("Form submission failed:", error);
    }

  }

  // function filterCustomKeys<T extends object>(inputObject: T): Partial<T> {
  //   const result: Partial<T> = {};

  //   for (const key in inputObject) {
  //     if (Object.prototype.hasOwnProperty.call(inputObject, key) && key.includes("custom") && key !== "custom_data") {
  //       result[key] = inputObject[key];
  //     }
  //   }

  //   return result;
  // }

  // function returnArrayOfCustomData(formData: FormData): customData[] {

  //   const array: customData[] = Object.keys(formData).map(key => ({
  //     field_name: key,
  //     field_value: formData[key]
  //   }));

  //   return array;
  // }


  const onSubmit: SubmitHandler<WorkEdit> = (data) => {

    // setWork(data);

    // const dataToSend = deleteEmptyFieldsFromObject(data);

    const savePromise = async () => {
      const savedData = await saveWork({
        workId: work!.id,
        workToSave: data,
      }).unwrap();
      setWork(savedData);

      if (location.state?.breadcrumbData) {

        const updatedWorks: DocumentWorkEdit[] = [...location.state.works];
        updatedWorks[location.state.workRowIndex] = {
          ...savedData,
          work_id: savedData.id,
          hours: 1,
          discount: 0,
          price: savedData.price ?? 0,
          net: savedData.price ?? 0,
          total: savedData.price ?? 0
        };


        navigate(getLastAvailableLink(location.state.breadcrumbData), {
          replace: true,
          state: {
            ...location.state,
            works: updatedWorks
          }
        });
      }
    }


    toast.promise(
      savePromise,
      {
        pending: 'מעדכן כרטיס...',
        success: 'הכרטיס עודכן בהצלחה',
        error: {
          render({ data }) {
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };

  const onDelete = async () => {
    console.log("delete, work:", work);

    const deletePromise = async () => {
      if (work) {
        await deleteWork(work.id!).unwrap();
        window.history.back();
      }
    }

    toast.promise(
      deletePromise,
      {
        pending: 'מוחק כרטיס...',
        success: 'הכרטיס נמחק בהצלחה',
        error: {
          render({ data }) {
            // When the promise reject, data will contains the error
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };

  function createState(){
    return {
      ...location.state,
    }
  }
    
  function createBreadcrumbData(state: any){

    const breadcrumbData = [
      {
        name: "כרטסות",
      },
      {
        name: "פריטים/מלאי",
        link: "/works",
      },
      {
        name: "עריכת כרטיס",
        link: `/works/${work?.id}`
      },
    ];

    return [...(editLastBread(state?.breadcrumbData, `/works/${work?.id}`, state) ?? breadcrumbData)];
  }

  function setStructure() {

    const currentState = createState();
    const breadcrumbData = createBreadcrumbData(currentState);

    navigate('/works/structure/form', {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }]
      }
    });
  }

  return (
    <StyledEditWork>
      {!workStructure ? <Skeleton variant="rectangular" height={500} animation="wave" sx={{ bgcolor: theme.colors.section_color }} /> :
        <>

          <div className="form">
            <div><div className="left">
              {/* <button onClick={setStructure}>הגדרת שדות</button> */}
              <Tooltip title="הגדרת שדות">
                <IconButton onClick={setStructure} sx={{ color: theme.colors.main_color }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </div></div>

            <div>
              <Typography variant="body1">פרטים:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["פרטים"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              <Typography variant="body1">יצרן ודגם:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["יצרן ודגם"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              <Typography variant="body1">הכנסות והוצאות:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["הכנסות והוצאות"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              {customFieldsExist && (<Typography variant="body1">שדות מותאמים אישית:</Typography>)}
              <div className="fields">
                {structureSortedByTitle?.["מותאם אישית"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

          </div>

          <div className="buttons">
            <div className="right">
              <Button variant="contained" onClick={saveTicket} disabled={form.formState.isSubmitting} >שמירה</Button>
              {/* <Button variant="outlined" onClick={returnFunc}>חזור</Button> */}
              <ReturnButton />
              <Button variant="outlined" color="error" onClick={() => setDialogIsOpen(true)} >מחיקה</Button>
            </div>
            <div className="left">
            </div>
          </div>

        </>
      }
      <AlertDialog
        open={dialogIsOpen}
        handleClose={() => { setDialogIsOpen(false) }}
        handleConfirm={onDelete}
        title="אישור מחיקה"
        message="אתה בטוח שאתה רוצה למחוק כרטיס זה?"
        confirm="מחיקה"
      />
    </StyledEditWork>
  )
}


export default EditWork